// Spawning data-grid and diagnostics tool windows. Extracted from App.tsx
// (mirroring utils/openGraphWindow) so non-App callers — e.g. the Tool Sets
// dialog's Load action — can open them without importing the whole App module.
// App.tsx re-exports both for existing consumers (TigerViewPanel, tests).

import { encodePrepopParam } from "./prepopTransfer";
import { appendLiveParam } from "./liveTabFetch";
import { PENDING_DIAG_TAG_QUERY_KEY } from "./buildTagDiagQuery";
import { getTigerResource } from "./tigerResource";
import type { WindowBounds } from "./openGraphWindow";
import type { DiagnosticsQueryParams } from "../types/dialog-types";

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

export function openDataGridWindow(tags?: string[], bounds?: WindowBounds, defaultConfig?: string) {
  // Tags are pipe-separated `config:tag` entries — the same format the
  // DataGridWindow's `?prepop=` parser expects.
  const prepop = tags && tags.length > 0 ? tags.join("|") : null;
  let baseUrl = prepop
    ? "/datagrid.html?" + encodePrepopParam(prepop)
    : "/datagrid.html";
  // Carry the opener's current turbine so the Add-Channel dialog defaults to it
  // (read back by readDefaultConfigParam) — the C++ data grid always uses
  // Ti_get_current_turbine(). No-op when not supplied.
  if (defaultConfig) {
    baseUrl += (baseUrl.includes("?") ? "&" : "?") + "config=" + encodeURIComponent(defaultConfig);
  }
  // Carry the opener's ?live=<config> so the window joins the same scoped session.
  const url = appendLiveParam(baseUrl);
  const title = getTigerResource("TIGERUI_DATAGRID_RES").trim();
  if (isTauri()) {
    import("@tauri-apps/api/webviewWindow").then(({ WebviewWindow }) => {
      // Random suffix: tool-set loading can spawn several windows in the same
      // millisecond, and window labels must be unique.
      const label = "datagrid-" + Date.now() + "-" + Math.random().toString(36).slice(2, 7);
      const wv = new WebviewWindow(label, {
        url,
        title,
        width: bounds?.width ?? 960,
        height: bounds?.height ?? 600,
        ...(bounds ? { x: bounds.x, y: bounds.y } : {}),
      });
      wv.once("tauri://created", () => console.log("Window created:", label));
      wv.once("tauri://error", (e) => console.error("Window error:", e));
    }).catch((err) => console.error("Import error:", err));
  } else {
    window.open(url, "_blank", "width=960,height=600,resizable=yes");
  }
}

export function openDiagnosticsWindow(
  currentTurbine?: string,
  tagChannel?: string,
  bounds?: WindowBounds,
  pendingQuery?: DiagnosticsQueryParams,
) {
  const base = "/diagnostics.html";
  const qs = new URLSearchParams();
  if (currentTurbine) qs.set("turbine", currentTurbine);
  // A `tag` param triggers the per-tag query auto-run in DiagnosticsWindow
  // (right-click "Display Diagnostics"), mirroring C++ set_tag_query.
  if (tagChannel) qs.set("tag", tagChannel);
  let url = qs.toString() ? base + "?" + qs.toString() : base;
  // A full saved query (tool-set Load) rides the URL as a prepop param. Each
  // spawned window gets its own param (long payloads land under a unique
  // localStorage key via encodePrepopParam), so a set containing several
  // diagnostics windows hands every window its own query — a single shared
  // localStorage key gets overwritten/consumed when windows race to mount.
  if (pendingQuery) {
    url += (url.includes("?") ? "&" : "?") + encodePrepopParam(JSON.stringify(pendingQuery));
  }
  // Carry the opener's ?live=<config> so the window joins the same scoped session.
  url = appendLiveParam(url);
  // Reliable handoff for the per-tag auto-query: the new webview's URL query
  // string isn't guaranteed to survive window creation, so also stash it in
  // localStorage (shared across same-origin webviews). A no-tag open clears any
  // stale handoff so the next plain Diagnostics window doesn't inherit it.
  try {
    if (tagChannel) {
      localStorage.setItem(
        PENDING_DIAG_TAG_QUERY_KEY,
        JSON.stringify({ turbine: currentTurbine ?? "", tag: tagChannel }),
      );
    } else {
      localStorage.removeItem(PENDING_DIAG_TAG_QUERY_KEY);
    }
  } catch {
    // localStorage unavailable — fall back to the URL param only.
  }
  const title = getTigerResource("TIGER_DIAG_TITLE").trim();
  if (isTauri()) {
    import("@tauri-apps/api/webviewWindow").then(({ WebviewWindow }) => {
      const label = "diagnostics-" + Date.now() + "-" + Math.random().toString(36).slice(2, 7);
      const wv = new WebviewWindow(label, {
        url,
        title,
        width: bounds?.width ?? 960,
        height: bounds?.height ?? 700,
        ...(bounds ? { x: bounds.x, y: bounds.y } : {}),
      });
      wv.once("tauri://created", () => console.log("Window created:", label));
      wv.once("tauri://error", (e) => console.error("Window error:", e));
    }).catch((err) => console.error("Import error:", err));
  } else {
    window.open(url, "_blank", "width=960,height=700,resizable=yes");
  }
}
